import { useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import PlanChangePreviewModal from "./PlanChangePreviewModal";

const FEATURES = {
  starter: [
    "Link 1 custom domain",
    "1 email alias (e.g. hello@)",
    "Forward to 1 recipient",
    "Forward to Gmail, Yahoo, Outlook, iCloud",
    "Send-as from your existing inbox",
    "SPF + DKIM included",
    "Email support",
  ],
  growth: [
    "Link 1 custom domain",
    "5 email aliases per domain (info@, support@, orders@…)",
    "Forward each alias to up to 3 recipients",
    "Higher daily send limits",
    "Priority deliverability monitoring",
    "Priority email support",
  ],
  pro: [
    "Link 1 custom domain",
    "Unlimited aliases per domain",
    "Unlimited forwarding recipients per alias",
    "Highest send limits",
    "Priority Email + Phone support",
  ],
};

function formatPrice(amount) {
  // Show as $2.50 if decimal, $5 if whole
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

export default function PricingCards() {
  const { user, config } = useAuth();
  const navigate = useNavigate();
  // Default the annual/monthly toggle to match the logged-in user's current cycle so
  // the "Current plan" badge is visible as soon as they arrive from "Change plan".
  const [annual, setAnnual] = useState(
    Boolean(user && typeof user === "object" && user.billing_cycle === "annual")
  );
  const plans = (config?.plans || [
    { id: "starter", name: "Starter", price_monthly: 3, price_annual: 30 },
    { id: "growth", name: "Growth", price_monthly: 6, price_annual: 60 },
    { id: "pro", name: "Pro", price_monthly: 9, price_annual: 90 },
  ]).filter(p => p.id !== "free");

  const [previewPackageId, setPreviewPackageId] = useState(null);

  const proceedToCheckout = async (package_id) => {
    // Fire GA4/Google Ads `begin_checkout` micro-conversion BEFORE Stripe
    // redirect so funnel analytics can attribute drop-off correctly.
    try {
      if (typeof window !== "undefined" && window.gtag && package_id) {
        const planId = package_id.split("_")[0];
        const cycle = package_id.split("_")[1] || "monthly";
        const planConf = plans.find((p) => p.id === planId);
        const value = planConf
          ? (cycle === "annual" ? planConf.price_annual : planConf.price_monthly)
          : 3;
        const planName = planId.charAt(0).toUpperCase() + planId.slice(1);
        window.gtag("event", "begin_checkout", {
          currency: "USD",
          value,
          items: [{
            item_id: package_id,
            item_name: `oly Inbox ${planName} (${cycle})`,
            price: value,
            quantity: 1,
          }],
        });
      }
    } catch (e) { /* analytics must never break checkout */ }

    try {
      const { data } = await api.post("/billing/checkout", {
        package_id,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSelect = async (planId) => {
    if (!user || typeof user !== "object") {
      navigate(`/signup?plan=${planId}&cycle=${annual ? "annual" : "monthly"}`);
      return;
    }
    const package_id = `${planId}_${annual ? "annual" : "monthly"}`;
    // Paying users get a no-surprises confirmation dialog before Stripe redirect.
    // First-plan users (plan === "free") skip it — they haven't paid anything yet.
    if (user.plan && user.plan !== "free") {
      setPreviewPackageId(package_id);
      return;
    }
    await proceedToCheckout(package_id);
  };

  return (
    <div className="space-y-10">
      <div className="flex items-center justify-center gap-3" data-testid="pricing-toggle">
        <span className={`text-sm font-medium ${!annual ? "text-[#0F172A]" : "text-slate-400"}`}>Monthly</span>
        <button
          onClick={() => setAnnual(!annual)}
          data-testid="pricing-cycle-toggle"
          className="relative h-7 w-14 rounded-full bg-slate-200 transition-colors data-[on=true]:bg-[#F26953]"
          data-on={annual}
          aria-label="Toggle annual billing"
        >
          <span
            className="absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform"
            style={{ transform: annual ? "translateX(28px)" : "translateX(0)" }}
          />
        </button>
        <span className={`text-sm font-medium ${annual ? "text-[#0F172A]" : "text-slate-400"}`}>
          Annual <span className="ml-1 inline-block text-xs font-semibold text-[#F26953] bg-[#FEE7E1] rounded px-1.5 py-0.5">save 17%</span>
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((p) => {
          const popular = p.id === "growth";
          const price = annual ? p.price_annual / 12 : p.price_monthly;
          const cycleLabel = annual ? "/mo, billed annually" : "/mo";
          const isCurrent = user && typeof user === "object" && user.plan === p.id
            && (user.billing_cycle === "annual") === annual;
          const isLoggedIn = user && typeof user === "object";
          return (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-2xl bg-white p-8 transition-all ${
                isCurrent
                  ? "border-2 border-emerald-500 shadow-lg ring-1 ring-emerald-500/20"
                  : popular
                  ? "border-2 border-[#F26953] shadow-lg ring-1 ring-[#F26953]/20"
                  : "border border-[#E2E8F0] shadow-sm hover:shadow-md"
              }`}
            >
              {isCurrent ? (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm" data-testid={`pricing-current-badge-${p.id}`}>
                  <Check className="h-3 w-3" /> Current plan
                </div>
              ) : popular ? (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-[#F26953] text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                  <Sparkles className="h-3 w-3" /> Most popular
                </div>
              ) : null}
              <h3 className="text-xl font-bold text-[#0F172A]">{p.name}</h3>
              <p className="text-sm text-[#475569] mt-1 min-h-[40px]">{p.tagline}</p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-5xl font-bold text-[#0F172A] font-[Outfit]">{formatPrice(price)}</span>
                <span className="text-sm text-[#475569]">{cycleLabel}</span>
              </div>
              {annual && (
                <div className="text-xs text-[#475569] mt-1">
                  ${p.price_annual}/year — save ${p.price_monthly * 12 - p.price_annual}
                </div>
              )}
              <button
                onClick={() => handleSelect(p.id)}
                disabled={isCurrent}
                data-testid={`pricing-cta-${p.id}`}
                className={`mt-6 w-full inline-flex items-center justify-center font-semibold py-3 rounded-lg transition-colors ${
                  isCurrent
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
                    : popular
                    ? "bg-[#F26953] text-white hover:bg-[#D85340] shadow-sm"
                    : "bg-[#0F172A] text-white hover:bg-slate-800"
                }`}
              >
                {isCurrent ? "Your current plan" : isLoggedIn ? `Switch to ${p.name}` : "Start with " + p.name}
              </button>
              <ul className="mt-6 space-y-3">
                {(FEATURES[p.id] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#475569]">
                    <Check className="h-4 w-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-slate-400">
        Cheaper than Google Workspace ($6+/user/month). Cancel anytime. No catch-all (we keep spam out).
      </p>

      <PlanChangePreviewModal
        open={!!previewPackageId}
        packageId={previewPackageId}
        onClose={() => setPreviewPackageId(null)}
        onConfirm={() => proceedToCheckout(previewPackageId)}
      />
    </div>
  );
}
